import { formatRelativeDayLabel } from '@acme/foundation';
import { useUnit } from 'effector-react';
import { FC } from 'react';
import { useChatWindowModel } from '../lib/chatwindowcontext';
import type { Message } from '../model';
import { MessageItem } from './message';

export interface ChatMessageListProps {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  variant?: 'default' | 'ticket';
}

export const ChatMessageList: FC<ChatMessageListProps> = (props) => {
  const variant = props.variant ?? 'default';
  const model = useChatWindowModel();
  const { messages, isLoadingMore, hasMoreMessages, isLoading } = useUnit({
    messages: model.$messages,
    isLoadingMore: model.$isLoadingMore,
    hasMoreMessages: model.$hasMoreMessages,
    isLoading: model.$isLoading,
  });

  const groupedMessages =
    variant === 'ticket'
      ? null
      : messages?.reduce(
          (groups, message, index) => {
            const date = formatRelativeDayLabel({
              date: new Date(message.sentAt as string),
            });
            if (!groups[date]) {
              groups[date] = [];
            }
            groups[date].push({ message, originalIndex: index });
            return groups;
          },
          {} as Record<
            string,
            Array<{ message: Message; originalIndex: number }>
          >
        );

  const containerClassName =
    variant === 'ticket'
      ? 'flex-1 overflow-y-auto -mx-6 px-6 py-2'
      : 'flex-1 overflow-y-auto px-4 py-2 bg-gray-50 border-t border-gray-200';

  return (
    <div
      ref={props.scrollContainerRef}
      className={containerClassName}
      data-chat-scroll-container
    >
      {isLoadingMore && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          <span className="ml-2 text-sm text-gray-500">
            Загрузка истории...
          </span>
        </div>
      )}

      {variant !== 'ticket' && !hasMoreMessages && messages.length > 0 && (
        <div className="text-center text-xs text-gray-500 py-4">
          Начало истории переписки
        </div>
      )}

      {variant === 'ticket' ? (
        <>
          {messages.map((message, index) => (
            <div key={message.id || index}>
              <MessageItem message={message} variant={variant} />
              {index < messages.length - 1 && (
                <div className="border-b border-gray-200 my-4 px-6 -mx-6" />
              )}
            </div>
          ))}
        </>
      ) : (
        <>
          {groupedMessages &&
            Object.entries(groupedMessages).map(([date, messages]) => (
              <div key={date} className="flex flex-col gap-4">
                <div className="text-center text-xs text-gray-500 my-4">
                  <span className="bg-white px-3 py-1 rounded-full border shadow-sm">
                    {date}
                  </span>
                </div>
                {messages.map(({ message, originalIndex }) => (
                  <MessageItem
                    key={`${date}-${originalIndex}`}
                    message={message}
                    variant={variant}
                  />
                ))}
              </div>
            ))}
        </>
      )}

      <div ref={props.messagesEndRef} data-chat-end />

      {isLoading && messages.length === 0 && (
        <div className="flex justify-center items-center h-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500" />
        </div>
      )}
    </div>
  );
};
